import Section from './Section'
import SectionHead from './SectionHead'

// THE positioning block: we are a technology platform, and that is why there is
// no agency overhead to pay (founder 2026-10-02).
//
// It is one component used on both the Get cover home and the pricing page,
// because the claim has to be worded identically everywhere it appears. It is
// also the visible half of something that already exists invisibly: the
// `disambiguatingDescription` on our Organization schema
// (lib/landingJsonLd.js NOT_AN_AGENCY) has said this to crawlers and AI
// summaries for months while the page itself never said it to a human.
//
// ⚖️ WHY THE WORDING IS CAREFUL, and do not loosen it:
//   · "not an employment agency or employment business" is a LEGAL position
//     (Employment Agencies Act 1973, and the IR35 reading), solicitor review
//     pending. It must match the schema and the footer word for word in meaning
//   · "no agency markup" is a structural fact ABOUT US: there’s no agency in
//     the middle, so there’s no agency margin. That is safe to say
//   · ⛔ "agencies charge 20% to 30%" is a claim about SOMEBODY ELSE’S pricing
//     and needs substantiation we do not have. It was removed from the FAQ for
//     exactly that reason (docs/MARKETING-SITE-SPEC.md §8). Do not reintroduce a
//     number here, or a saving, without evidence on file
//
// ⛔ VOICE. The founder’s own framing of this was "eliminates agency overhead",
// "book talent with confidence", "immediate shift needs". That is deck language
// and the house voice is not written that way: outcomes, short sentences, no
// jargon. "Talent" is also wrong on the merits, because the people here are self
// employed professionals choosing work, not a resource being sourced.

const PILLARS = [
  {
    title: 'Checked before they can accept',
    body: 'Identity, right to work, professional registration and the documents the role needs. Verified once, then watched, so nothing lapses quietly between shifts.',
  },
  {
    title: 'You deal with them directly',
    body: 'You set the rate, you see who is coming, and they agree the work with you. Nobody negotiates on your behalf and no one takes a cut of it.',
  },
  {
    title: 'No agency in the middle',
    body: "There’s no agency margin on a TempDash shift, because there’s no agency. You see the full cost before you post and nothing is added after.",
  },
]

export default function NotAnAgency({ ground = 'surface' }) {
  return (
    <Section ground={ground}>
      <div className="md:flex md:items-start md:gap-20">
        <div className="md:w-[440px] md:shrink-0">
          <SectionHead eyebrow="What TempDash is" title={<>Software, not an agency.</>} />
          <p className="mt-5 text-[19px] leading-relaxed text-ink-muted">
            TempDash is the technology that connects a workplace to a checked, self employed
            professional, and handles the compliance and the money in between. We do not employ
            anyone, we do not supply anyone, and we do not set what they are paid.
          </p>
        </div>

        <ul className="mt-10 flex-1 space-y-0 md:mt-0">
          {PILLARS.map((p, i) => (
            <li key={p.title} className={`border-hairline py-6 ${i === 0 ? 'border-t md:border-t-0 md:pt-0' : 'border-t'}`}>
              <h3 className="text-[21px] font-semibold">{p.title}</h3>
              <p className="mt-2 text-[17px] leading-relaxed text-ink-muted">{p.body}</p>
            </li>
          ))}
        </ul>
      </div>
    </Section>
  )
}
